import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { AppShell } from '../components/Layout';
import EmptyState from '../components/EmptyState';
import api from '../services/api';
import avatarTint from '../utils/avatarTint';
import Icon from '../components/Icon';
import { sentenceCase, dateHeading, time12 } from '../utils/listFormat';

// Shared badge vocabulary from index.css — identical styling to the navigator
// portal's appointment statuses.
const STATUS_META = {
  SCHEDULED:  { label: 'Scheduled',  cls: 'badge-confirmed' },
  CONFIRMED:  { label: 'Confirmed',  cls: 'badge-active'    },
  COMPLETED:  { label: 'Completed',  cls: 'badge-active'    },
  CANCELLED:  { label: 'Cancelled',  cls: 'badge-inactive'  },
  PENDING:    { label: 'Pending',    cls: 'badge-pending'   },
  ARRIVED:   { label: 'Arrived',   cls: 'badge-pending'   },
  IN_CONSULTATION: { label: 'In consultation', cls: 'badge-purple' },
  NO_SHOW:    { label: 'No show',    cls: 'badge-inactive'  },
};

function initials(first = '', last = '') {
  return `${first[0] || ''}${last[0] || ''}`.toUpperCase();
}

// Phone layout (max-width 768px) — see the "Doctor My Patients / Appointments
// lists — phone layout" block in index.css. Everything tagged mlist-* or
// dr-mlist-* is either restyled there or shown on only one side of the
// breakpoint (mlist-phone-only / mlist-desktop-only); the desktop list is
// untouched. The rows stay button.card-row (the doctor walkthrough finds
// them by that).
const patientName = (a) => [a.patient?.firstName, a.patient?.lastName].filter(Boolean).join(' ');

// The search box matches the patient's name and the appointment type, both as
// stored ("follow_up") and as read ("Follow up"); nothing else on the row.
const searchText = (a) => {
  const type = a.appointmentType || 'Consultation';
  return `${patientName(a)} ${type} ${sentenceCase(type)}`.toLowerCase();
};

export default function DoctorAppointmentsPage() {
  const navigate = useNavigate();
  const { user }  = useAuth();
  const { toast } = useToast();

  const [appointments, setAppointments] = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [filter,       setFilter]       = useState('upcoming');
  const [search,       setSearch]       = useState('');
  const searchRef = useRef(null);

  // Dynamic page title
  useEffect(() => {
    document.title = 'Appointments — Previu';
    return () => { document.title = 'Previu — Patient Management'; };
  }, []);

  const fetchAppointments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch all appointments then filter by doctor email server-side
      // GET /api/appointments?doctorEmail=&filter=
      const res = await api.get('/doctor/appointments', {
        params: {
          doctorEmail: user?.email,
          filter,
        },
      });
      // Handle both paged and plain array responses
      const data = res.data;
      setAppointments(data.content || data || []);
    } catch {
      setError('Could not load appointments. Please try again.');
      toast.error('Could not load appointments — check your connection');
    } finally {
      setLoading(false);
    }
  }, [user?.email, filter]);

  useEffect(() => { fetchAppointments(); }, [fetchAppointments]);

  // Backend returns pre-filtered list; use directly. The search narrows the
  // loaded rows of the current tab in the browser (the endpoint returns every
  // appointment in the tab), and the text is kept across tab switches.
  const needle = search.trim().toLowerCase();
  const displayed = useMemo(() => (needle
    ? appointments.filter(a => searchText(a).includes(needle))
    : appointments), [appointments, needle]);

  const clearSearch = () => {
    setSearch('');
    searchRef.current?.focus();
  };

  return (
    <AppShell breadcrumbs={[{ label: 'Appointments' }]}>

      {/* Header — canonical .page-header */}
      <div className="page-header mlist-header fade-in-up">
        <div className="mlist-header-text">
          <div className="page-title">Appointments</div>
          <div className="page-subtitle mlist-desktop-only">Your scheduled appointments</div>
          {!loading && (
            <div className="mlist-count mlist-phone-only">
              {displayed.length} appointment{displayed.length !== 1 ? 's' : ''}
            </div>
          )}
        </div>
      </div>

      {/* One search box at every width, above the tabs. The result line is a
          live region that stays mounted (so screen readers announce changes)
          and is taken out of the layout while the box is empty. */}
      <div className="toolbar mlist-toolbar">
        <div className="search-input-wrap">
          <label className="mlist-sr-only" htmlFor="doctor-appointment-search">Search appointments</label>
          <span className="search-icon"><Icon name="search" size={15} /></span>
          <input
            id="doctor-appointment-search"
            ref={searchRef}
            className="search-input"
            type="text"
            placeholder="Search by patient or type…"
            maxLength={100}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <div
          id="doctor-appointment-search-count"
          className={needle && !loading ? 'dr-appt-search-count' : 'mlist-sr-only'}
          aria-live="polite"
        >
          {needle && !loading
            ? `Showing ${displayed.length} of ${appointments.length} appointment${appointments.length !== 1 ? 's' : ''}`
            : ''}
        </div>
      </div>

      {/* Filter tabs — canonical .tabs-bar/.tab-btn, same as PatientDetailPage */}
      <div className="tabs-bar dr-mlist-tabs fade-in-up delay-1">
        {/* DEF-025 — "Cancelled future appointment appears under Upcoming
            count." The server's filter was date-only, so a cancelled
            appointment still counted as upcoming while its date was in the
            future; AppointmentBucket now considers status too. A Cancelled tab
            is added here so those appointments remain reachable rather than
            disappearing from the doctor's view along with the bug. */}
        {['upcoming', 'past', 'cancelled', 'all'].map(f => (
          <button
            key={f}
            className={`tab-btn ${filter === f ? 'active' : ''}`}
            onClick={() => setFilter(f)}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
            {f === filter && appointments.length > 0 && (
              <span className="tab-count">{appointments.length}</span>
            )}
          </button>
        ))}
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {loading ? (
        <div className="skeleton-list">
          {[...Array(4)].map((_, i) => <div key={i} className="skeleton skeleton--row" />)}
        </div>
      ) : displayed.length === 0 && needle ? (
        <EmptyState
          icon="calendar"
          title="No appointments match"
          message="Try a different patient name or appointment type."
          action={{ label: 'Clear search', onClick: clearSearch }}
        />
      ) : displayed.length === 0 ? (
        <EmptyState
          icon="calendar"
          title={filter === 'upcoming' ? 'No upcoming appointments'
            : filter === 'past' ? 'No past appointments'
            : filter === 'cancelled' ? 'No cancelled appointments'
            : 'No appointments yet'}
          message="Appointments appear here once a navigator schedules one for your patients."
        />
      ) : (
        <div className="card-list dr-mlist-appts fade-in-up delay-2">
          {displayed.map((a, i) => {
            const status = STATUS_META[a.status] || STATUS_META.SCHEDULED;
            // Date headings (phone only) go in wherever the date changes, in
            // the server's own order — rows are never re-sorted.
            const day = a.appointmentDate?.slice(0, 10) || '';
            const newDay = i === 0 || day !== (displayed[i - 1].appointmentDate?.slice(0, 10) || '');
            return (
              <React.Fragment key={a.id}>
                {newDay && <div className="dr-mlist-date mlist-phone-only">{dateHeading(day)}</div>}
                <button
                  className="card-row dr-mlist-appt"
                  type="button"
                  onClick={() => navigate(`/doctor/appointments/${a.id}`)}
                  aria-label={`View appointment with ${a.patient?.firstName} ${a.patient?.lastName} on ${a.appointmentDate}`}
                >
                  <div className="date-chip mlist-desktop-only">
                    <div className="date-chip-day">
                      {a.appointmentDate
                        ? new Date(a.appointmentDate).toLocaleDateString('en-IN', { day: 'numeric' })
                        : '—'}
                    </div>
                    <div className="date-chip-month">
                      {a.appointmentDate
                        ? new Date(a.appointmentDate).toLocaleDateString('en-IN', { month: 'short' })
                        : ''}
                    </div>
                  </div>
                  <div className="card-row-divider mlist-desktop-only" />
                  <span className="mlist-time mlist-phone-only">{time12(a.appointmentTime)}</span>
                  <div className="card-row-body">
                    <div className="dr-mlist-appt-who" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div className={`avatar avatar--sm mlist-desktop-only ${avatarTint(a.patient?.firstName || '')}`}
                        aria-hidden="true">
                        {initials(a.patient?.firstName, a.patient?.lastName)}
                      </div>
                      <div className="dr-mlist-appt-text">
                        <div className="card-row-title">
                          {a.patient?.firstName} {a.patient?.lastName}
                        </div>
                        <div className="card-row-meta mlist-desktop-only">
                          {a.appointmentType || 'Consultation'}
                          {a.appointmentTime && ` · ${a.appointmentTime}`}
                        </div>
                        <div className="card-row-meta mlist-phone-only">
                          {sentenceCase(a.appointmentType || 'Consultation')}
                        </div>
                      </div>
                    </div>
                  </div>
                  <span className={`badge ${status.cls}`}>{status.label}</span>
                  <span className="mlist-chevron mlist-phone-only" aria-hidden="true"><Icon name="chevronRight" size={18} /></span>
                </button>
              </React.Fragment>
            );
          })}
        </div>
      )}
    </AppShell>
  );
}
